// Considered: no pagination atom or molecule exists in the design system (PeriodStatusButtonGroup is not a
// paginator). See spec Reuse Audit. Chrome deliberately minimal to match the handoff: inline link-style
// Prev/Next with arrows + a read-only Page X / Y indicator. No input chrome.
import React from 'react';
import { useTranslation } from 'localization';

export interface RollupRosterPaginationProps {
  /** Zero-based page index. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const RollupRosterPagination: React.FC<RollupRosterPaginationProps> = ({ page, pageSize, total, onPageChange }) => {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  if (total <= 0) return null;

  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);
  const atFirst = page <= 0;
  const atLast = page + 1 >= pageCount;

  return (
    <nav className="rollup-roster__pagination" aria-label={t('statusFactors.roster.paginationLabel', 'Roster pages')}>
      <span className="rollup-roster__range" data-testid="roster-range">
        {t('statusFactors.roster.range', 'Showing {{first}}–{{last}} of {{total}}', { first, last, total })}
      </span>
      <div className="rollup-roster__pager">
        <button
          type="button"
          className="rollup-roster__pager-link"
          disabled={atFirst}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('statusFactors.roster.previousPage', 'Previous page')}
        >
          <span aria-hidden="true">←</span> {t('statusFactors.roster.prev', 'Prev')}
        </button>
        <span className="rollup-roster__pager-indicator" aria-live="polite">
          {t('statusFactors.roster.pageOf', 'Page {{current}} / {{count}}', { current: page + 1, count: pageCount })}
        </span>
        <button
          type="button"
          className="rollup-roster__pager-link"
          disabled={atLast}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('statusFactors.roster.nextPage', 'Next page')}
        >
          {t('statusFactors.roster.next', 'Next')} <span aria-hidden="true">→</span>
        </button>
      </div>
    </nav>
  );
};

export default RollupRosterPagination;
